import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { ROUTES } from '@app/Router';
import { col } from '@app/style';
import { RootStackParamList } from '@app/types';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import React from 'react';
import { useCheckPermissions } from '@app/hooks/useCheckPermissions';

type MenuItemProps = {
  label: string;
  Icon: (...args: any[]) => JSX.Element;
  activeOnRouteNames?: (keyof RootStackParamList)[];
};

export const MenuItem = ({ label, Icon, activeOnRouteNames = [] }: MenuItemProps) => {
  const navigation = useNavigation();
  const checkPermissions = useCheckPermissions();
  const screenName = useNavigationState((state) => state?.routes[state.index]?.name) as keyof RootStackParamList;

  const permissions = React.useMemo(
    () =>
      ROUTES
        .filter((route) => activeOnRouteNames.includes(route.name))
        .flatMap((route) => route.permissions),
    [activeOnRouteNames]
  );

  const hasPermissions = permissions.some(permission => checkPermissions([permission]));

  const availableRoutes = activeOnRouteNames.filter((routeName) => {
    const route = ROUTES.find((r) => r.name === routeName);

    return checkPermissions(route?.permissions ?? [])
  });

  const routeName = availableRoutes[0];

  const isActive = (!!screenName && screenName === routeName || activeOnRouteNames.includes(screenName));

  const iconStyle = { strokePath: isActive ? '#0089C8' : '#FFF' };

  const goTo = () => {
    if (routeName) {
      // @ts-expect-error
      navigation.navigate(routeName);
    }
  }

  if (!hasPermissions && activeOnRouteNames.length === permissions.length) {
    return null;
  }

  return (
    <MenuItemHolder onPress={goTo}>
      <MenuIcon isActive={isActive}>
        <Icon {...iconStyle} />
      </MenuIcon>
      <MenuLabel>{label}</MenuLabel>
    </MenuItemHolder>
  );
};

export const Menu = styled.View({});

export const MenuItemHolder = styled.TouchableOpacity(({ theme }) => ({
  ...col,
  justifyContent: 'center',
  alignItems: 'center',
  paddingVertical: normalize(27),
}));

export const MenuIcon = styled.View<{ isActive?: boolean }>(({ props }) => ({
  height: props.isActive ? normalize(51) : normalize(34),
  width: props.isActive ? normalize(51) : normalize(34),
}));

export const MenuLabel = styled.Text(({ theme }) => ({
  display: 'none',
}));
